import React, { useRef, useState } from 'react';
import { fabric } from 'fabric';
import { useTranslation } from 'react-i18next';
import { ImageUp, Loader2, Replace, Wallpaper } from 'lucide-react';

import { isImageObject } from '../fabric-setup';
import { coverFit } from '../objects';
import type { DesignEditorApi } from '../useDesignEditor';
import { SectionLabel } from '../ui';

export interface PanelImage { url: string; thumb: string }

type Cell = { x: number; y: number; w: number; h: number };
type CellsFn = (n: number, W: number, H: number, g: number) => Cell[];

/** Grid layouts for the image layers already on the artboard. */
const LAYOUTS: Array<{ id: string; min: number; max: number; preview: Array<[number, number, number, number]>; cells: CellsFn }> = [
    { id: 'split-h', min: 2, max: 2, preview: [[0, 0, 0.5, 1], [0.5, 0, 0.5, 1]], cells: (_, W, H, g) => [{ x: 0, y: 0, w: (W - g) / 2, h: H }, { x: (W + g) / 2, y: 0, w: (W - g) / 2, h: H }] },
    { id: 'split-v', min: 2, max: 2, preview: [[0, 0, 1, 0.5], [0, 0.5, 1, 0.5]], cells: (_, W, H, g) => [{ x: 0, y: 0, w: W, h: (H - g) / 2 }, { x: 0, y: (H + g) / 2, w: W, h: (H - g) / 2 }] },
    { id: '1-plus-2', min: 3, max: 3, preview: [[0, 0, 0.5, 1], [0.5, 0, 0.5, 0.5], [0.5, 0.5, 0.5, 0.5]], cells: (_, W, H, g) => [{ x: 0, y: 0, w: (W - g) / 2, h: H }, { x: (W + g) / 2, y: 0, w: (W - g) / 2, h: (H - g) / 2 }, { x: (W + g) / 2, y: (H + g) / 2, w: (W - g) / 2, h: (H - g) / 2 }] },
    { id: '3-col', min: 3, max: 3, preview: [[0, 0, 1 / 3, 1], [1 / 3, 0, 1 / 3, 1], [2 / 3, 0, 1 / 3, 1]], cells: (_, W, H, g) => [0, 1, 2].map((i) => ({ x: i * ((W - 2 * g) / 3 + g), y: 0, w: (W - 2 * g) / 3, h: H })) },
    { id: '2x2', min: 4, max: 4, preview: [[0, 0, 0.5, 0.5], [0.5, 0, 0.5, 0.5], [0, 0.5, 0.5, 0.5], [0.5, 0.5, 0.5, 0.5]], cells: (_, W, H, g) => [0, 1, 2, 3].map((i) => ({ x: (i % 2) * ((W + g) / 2), y: Math.floor(i / 2) * ((H + g) / 2), w: (W - g) / 2, h: (H - g) / 2 })) },
    {
        id: 'auto', min: 2, max: 99,
        preview: [[0, 0, 1 / 3, 0.5], [1 / 3, 0, 1 / 3, 0.5], [2 / 3, 0, 1 / 3, 0.5], [0, 0.5, 1 / 3, 0.5], [1 / 3, 0.5, 1 / 3, 0.5], [2 / 3, 0.5, 1 / 3, 0.5]],
        cells: (n, W, H, g) => {
            const cols = Math.ceil(Math.sqrt(n));
            const rows = Math.ceil(n / cols);
            const cw = (W - g * (cols - 1)) / cols;
            const ch = (H - g * (rows - 1)) / rows;
            return Array.from({ length: n }, (_, i) => ({ x: (i % cols) * (cw + g), y: Math.floor(i / cols) * (ch + g), w: cw, h: ch }));
        },
    },
];

export const ImagesPanel: React.FC<{
    ed: DesignEditorApi;
    projectImages: PanelImage[];
    uploads: PanelImage[];
    loadingProject: boolean;
    onUpload: (file: File) => Promise<string | null>;
}> = ({ ed, projectImages, uploads, loadingProject, onUpload }) => {
    const { t } = useTranslation();
    const fileRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [gutter, setGutter] = useState(true);

    const selectedImage = ed.active && isImageObject(ed.active) && !(ed.active as any).csLocked ? (ed.active as fabric.Image) : null;
    const imageLayers = (ed.canvas?.getObjects() || []).filter((o) => isImageObject(o) && (o as any).csRole !== 'logo' && (o as any).csRole !== 'background' && !(o as any).csTemp) as fabric.Image[];

    const use = (url: string) => {
        if (selectedImage) void ed.replaceImage(url, selectedImage);
        else void ed.addImage(url);
    };

    const applyLayout = (cells: CellsFn) => {
        const c = ed.canvas;
        if (!c) return;
        const a = ed.artboard;
        const u = Math.min(a.w, a.h);
        const g = gutter ? u * 0.015 : 0;
        const pad = gutter ? u * 0.015 : 0;
        const boxes = cells(imageLayers.length, a.w - pad * 2, a.h - pad * 2, g);
        imageLayers.forEach((img, i) => {
            const b = boxes[i];
            if (!b) return;
            img.set({ clipPath: undefined } as any);
            coverFit(img, { x: b.x + pad, y: b.y + pad, w: b.w, h: b.h });
        });
        c.requestRenderAll();
        ed.commit();
        ed.refresh();
    };

    const Grid: React.FC<{ items: PanelImage[] }> = ({ items }) => (
        <div className="grid grid-cols-2 gap-1.5">
            {items.map((img) => (
                <div key={img.url} className="group relative aspect-square overflow-hidden rounded-lg bg-muted ring-1 ring-border">
                    <button
                        type="button"
                        onClick={() => use(img.url)}
                        title={selectedImage ? t('DESIGN_EDITOR.IMAGE_REPLACE') : t('DESIGN_EDITOR.IMAGE_ADD')}
                        className="absolute inset-0 cursor-pointer"
                    >
                        <img src={img.thumb} alt="" loading="lazy" className="size-full object-cover transition-transform group-hover:scale-[1.03]" />
                    </button>
                    <button
                        type="button"
                        onClick={() => { void ed.setBackgroundFromUrl(img.url); }}
                        title={t('DESIGN_EDITOR.IMAGE_SET_BACKGROUND')}
                        aria-label={t('DESIGN_EDITOR.IMAGE_SET_BACKGROUND')}
                        className="absolute end-1.5 bottom-1.5 flex size-7 cursor-pointer items-center justify-center rounded-md bg-background/90 text-foreground opacity-0 shadow-xs transition-opacity group-hover:opacity-100 [&_svg]:size-3.5"
                    >
                        <Wallpaper />
                    </button>
                </div>
            ))}
        </div>
    );

    return (
        <div className="flex flex-col gap-5">
            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (fileRef.current) fileRef.current.value = '';
                    if (!file) return;
                    setUploading(true);
                    try {
                        const url = await onUpload(file);
                        if (url) use(url);
                    } finally {
                        setUploading(false);
                    }
                }}
            />
            <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-foreground text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-60 [&_svg]:size-4"
            >
                {uploading ? <Loader2 className="animate-spin" /> : <ImageUp />}
                {t('DESIGN_EDITOR.UPLOAD_IMAGE')}
            </button>

            {selectedImage && (
                <div className="flex items-start gap-2 rounded-xl bg-foreground/[0.04] p-3 text-xs leading-snug text-foreground [&_svg]:mt-0.5 [&_svg]:size-3.5 [&_svg]:shrink-0">
                    <Replace />
                    {t('DESIGN_EDITOR.IMAGE_REPLACE_HINT')}
                </div>
            )}

            {uploads.length > 0 && (
                <div className="flex flex-col gap-2">
                    <SectionLabel>{t('DESIGN_EDITOR.UPLOADS')}</SectionLabel>
                    <Grid items={uploads} />
                </div>
            )}

            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.PROJECT_IMAGES')}</SectionLabel>
                {loadingProject && <div className="flex justify-center py-4"><Loader2 className="size-4 animate-spin text-muted-foreground" /></div>}
                {!loadingProject && projectImages.length === 0 && (
                    <div className="text-xs text-muted-foreground">{t('DESIGN_EDITOR.PROJECT_IMAGES_EMPTY')}</div>
                )}
                <Grid items={projectImages} />
            </div>

            {imageLayers.length >= 2 && (
                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <SectionLabel>{t('DESIGN_EDITOR.GRID_LAYOUTS')}</SectionLabel>
                        <button type="button" onClick={() => setGutter((g) => !g)} className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground">
                            {gutter ? t('DESIGN_EDITOR.GRID_GUTTER_ON') : t('DESIGN_EDITOR.GRID_GUTTER_OFF')}
                        </button>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                        {LAYOUTS.filter((l) => imageLayers.length >= l.min && imageLayers.length <= l.max).map((l) => (
                            <button
                                key={l.id}
                                type="button"
                                onClick={() => applyLayout(l.cells)}
                                title={t(`DESIGN_EDITOR.GRID_${l.id.toUpperCase().replace(/-/g, '_')}`)}
                                className="flex aspect-square cursor-pointer items-center justify-center rounded-lg border bg-background p-2 transition-colors hover:border-foreground/40"
                            >
                                <svg viewBox="0 0 40 40" className="size-full">
                                    {l.preview.map(([x, y, w, hh], i) => (
                                        <rect key={i} x={x * 40 + 1} y={y * 40 + 1} width={w * 40 - 2} height={hh * 40 - 2} rx={1.5} className="fill-muted-foreground/45" />
                                    ))}
                                </svg>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
